import { useState, useEffect } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';

export default function SuperAdminDashboard({ user, onLogout, onImpersonate }) {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [units, setUnits] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [filter, setFilter] = useState('all'); // all, active, inactive, deleted

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, portfoliosRes, unitsRes, requestsRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/users'),
        api.get('/admin/portfolios'),
        api.get('/admin/units'),
        api.get('/admin/status-requests')
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setPortfolios(portfoliosRes.data);
      setUnits(unitsRes.data);
      setRequests(requestsRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (userId) => {
    try {
      const response = await api.post(`/admin/users/${userId}/toggle-active`);
      toast.success(response.data.message);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle user status');
    }
  };

  const handleSoftDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This is a soft delete and can be reversed.')) {
      return;
    }
    
    try {
      await api.post(`/admin/users/${userId}/soft-delete`);
      toast.success('User deleted successfully');
      setShowUserModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleRestore = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/restore`);
      toast.success('User restored successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore user');
    }
  };

  const handleImpersonate = async (userId) => {
    try {
      const response = await api.post(`/admin/impersonate/${userId}`);
      toast.success(`Now impersonating ${response.data.user.email}`);
      onImpersonate(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to impersonate user');
    }
  };

  const openUserModal = (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  const filteredUsers = users.filter(u => {
    if (filter === 'all') return !u.is_deleted;
    if (filter === 'active') return u.is_active && !u.is_deleted;
    if (filter === 'inactive') return !u.is_active && !u.is_deleted;
    if (filter === 'deleted') return u.is_deleted;
    return true;
  });

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super-admin': return 'bg-purple-500';
      case 'owner': return 'bg-blue-500';
      case 'agent': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusBadge = (user) => {
    if (user.is_deleted) return <Badge variant="destructive">Deleted</Badge>;
    if (!user.is_active) return <Badge variant="secondary">Inactive</Badge>;
    return <Badge variant="default" className="bg-green-500">Active</Badge>;
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="dashboard" data-testid="super-admin-dashboard">
      <div className="dashboard-header">
        <h1 data-testid="dashboard-title" className="brand-title">
          <img src="/favicon.png" alt="" className="brand-mark" />
          <span className="brand-text">Unit Relay</span>
          <span className="role-badge" style={{background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)'}}>Super Admin</span>
        </h1>
        <div className="header-actions">
          <span className="user-email" data-testid="user-email">{user.email}</span>
          <button className="btn-secondary" onClick={onLogout} data-testid="logout-button">
            Logout
          </button>
        </div>
      </div>

      <div className="dashboard-content">
        {/* Stats Cards */}
        {stats && (
          <div className="status-summary-grid" data-testid="admin-stats" style={{ marginBottom: '24px' }}>
            <div className="status-card" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}>
              <div className="status-card-count">{stats.total_users}</div>
              <div className="status-card-label">Total Users</div>
            </div>
            <div className="status-card" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
              <div className="status-card-count">{stats.active_users}</div>
              <div className="status-card-label">Active Users</div>
            </div>
            <div className="status-card" style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' }}>
              <div className="status-card-count">{stats.total_portfolios}</div>
              <div className="status-card-label">Portfolios</div>
            </div>
            <div className="status-card" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
              <div className="status-card-count">{stats.total_units}</div>
              <div className="status-card-label">Total Units</div>
            </div>
            <div className="status-card" style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>
              <div className="status-card-count">{stats.pending_requests}</div>
              <div className="status-card-label">Pending Requests</div>
            </div>
          </div>
        )}

        <Tabs defaultValue="users" className="dashboard-tabs">
          <TabsList className="tabs-nav" data-testid="admin-tabs">
            <TabsTrigger value="users" data-testid="users-tab">Users ({users.length})</TabsTrigger>
            <TabsTrigger value="portfolios" data-testid="portfolios-tab">Portfolios ({portfolios.length})</TabsTrigger>
            <TabsTrigger value="units" data-testid="units-tab">All Units ({units.length})</TabsTrigger>
            <TabsTrigger value="requests" data-testid="requests-tab">All Requests ({requests.length})</TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="dashboard-section">
              <div className="section-header">
                <h2>All Users</h2>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {['all', 'active', 'inactive', 'deleted'].map((f) => (
                    <button
                      key={f}
                      className={`btn-sm ${filter === f ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => setFilter(f)}
                      style={{ textTransform: 'capitalize' }}
                    >
                      {f}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Portfolio</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((u) => (
                      <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                        <TableCell className="font-medium">{u.email}</TableCell>
                        <TableCell>
                          <Badge className={getRoleBadgeColor(u.role)}>{u.role}</Badge>
                        </TableCell>
                        <TableCell>
                          {u.portfolio_token ? (
                            <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                              {u.portfolio_token}
                            </span>
                          ) : '-'}
                        </TableCell>
                        <TableCell>{getStatusBadge(u)}</TableCell>
                        <TableCell>{new Date(u.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            {u.role !== 'super-admin' && !u.is_deleted && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleImpersonate(u.id)}
                                  data-testid={`impersonate-${u.id}`}
                                >
                                  Impersonate
                                </Button>
                                <Button
                                  size="sm"
                                  variant={u.is_active ? "secondary" : "default"}
                                  onClick={() => handleToggleActive(u.id)}
                                  data-testid={`toggle-active-${u.id}`}
                                >
                                  {u.is_active ? 'Deactivate' : 'Activate'}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => openUserModal(u)}
                                  data-testid={`delete-${u.id}`}
                                >
                                  Delete
                                </Button>
                              </>
                            )}
                            {u.is_deleted && (
                              <Button
                                size="sm"
                                variant="default"
                                onClick={() => handleRestore(u.id)}
                                data-testid={`restore-${u.id}`}
                              >
                                Restore
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* Portfolios Tab */}
          <TabsContent value="portfolios">
            <div className="dashboard-section">
              <div className="section-header">
                <h2>All Portfolios</h2>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Token</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Owner</TableHead>
                      <TableHead>Users</TableHead>
                      <TableHead>Units</TableHead>
                      <TableHead>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {portfolios.map((p) => (
                      <TableRow key={p.id} data-testid={`portfolio-row-${p.id}`}>
                        <TableCell>
                          <span style={{ fontFamily: 'monospace', fontWeight: '600', fontSize: '14px' }}>
                            {p.token}
                          </span>
                        </TableCell>
                        <TableCell className="font-medium">{p.name}</TableCell>
                        <TableCell>{p.owner_email || '-'}</TableCell>
                        <TableCell>{p.user_count}</TableCell>
                        <TableCell>{p.unit_count}</TableCell>
                        <TableCell>{new Date(p.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* Units Tab */}
          <TabsContent value="units">
            <div className="dashboard-section">
              <div className="section-header">
                <h2>All Units</h2>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Portfolio</TableHead>
                      <TableHead>Address</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead>BD/BA</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Hidden</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {units.slice(0, 100).map((unit) => (
                      <TableRow key={unit.id} data-testid={`unit-row-${unit.id}`}>
                        <TableCell>
                          <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                            {unit.portfolio_token || '-'}
                          </span>
                        </TableCell>
                        <TableCell>{unit.address || '-'}</TableCell>
                        <TableCell className="font-medium">{unit.unit}</TableCell>
                        <TableCell>{unit.bd_ba}</TableCell>
                        <TableCell>
                          <span className={`status-badge status-${unit.status.toLowerCase().replace(/\s+/g, '-')}`}>
                            {unit.status}
                          </span>
                        </TableCell>
                        <TableCell>{unit.hidden ? 'Yes' : 'No'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {units.length > 100 && (
                  <p style={{ textAlign: 'center', color: '#64748b', marginTop: '16px' }}>
                    Showing first 100 of {units.length} units
                  </p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Requests Tab */}
          <TabsContent value="requests">
            <div className="dashboard-section">
              <div className="section-header">
                <h2>All Status Change Requests</h2>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Portfolio</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead>Agent</TableHead>
                      <TableHead>New Status</TableHead>
                      <TableHead>Tenant</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Requested</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requests.map((req) => (
                      <TableRow key={req.id} data-testid={`request-row-${req.id}`}>
                        <TableCell>
                          <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                            {req.portfolio_token || '-'}
                          </span>
                        </TableCell>
                        <TableCell>{req.unit_info?.unit || '-'}</TableCell>
                        <TableCell>{req.agent_email}</TableCell>
                        <TableCell>{req.new_status}</TableCell>
                        <TableCell>{req.tenant_name}</TableCell>
                        <TableCell>
                          <Badge variant={req.request_status === 'pending' ? 'secondary' : req.request_status === 'approved' ? 'default' : 'destructive'}>
                            {req.request_status}
                          </Badge>
                        </TableCell>
                        <TableCell>{new Date(req.requested_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Delete Confirmation Modal */}
      <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
        <DialogContent data-testid="delete-modal">
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div style={{ padding: '16px 0' }}>
              <p>Are you sure you want to delete <strong>{selectedUser.email}</strong>?</p>
              <p style={{ color: '#64748b', fontSize: '14px', marginTop: '8px' }}>
                This is a soft delete. The user will be marked as deleted but their data will be retained.
                You can restore the user later if needed.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserModal(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => handleSoftDelete(selectedUser?.id)}>
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
